import { useEffect, useState, type ReactNode } from 'react';
import { Command } from 'cmdk';
import { useTranslation } from 'react-i18next';
import {
  Pin,
  Lightbulb,
  CheckSquare,
  Database,
  FolderKanban,
  ListPlus,
  Download,
  Search,
  Upload,
  CalendarDays,
  FilePlus2,
  FileText,
  LayoutDashboard,
  Waypoints,
  LayoutTemplate,
  PanelLeft,
  Plus,
  Settings,
  Sparkles,
  Trash2,
  FolderTree,
  Puzzle,
  Users,
} from 'lucide-react';
import { seedWorkspace, type PageRecord, type SearchHit } from '@nexus/store';
import { createDatabase } from '@nexus/workspace';
import { exportToZip } from '../lib/export';
import { Snippet } from './Snippet';
import { useStore } from '../db/context';
import { ensureDailyNote } from '../lib/pages';
import { listAllTemplates, type TemplateInfo } from '@nexus/workspace';
import { openUseTemplate } from '../templates/state';
import { pluginToast, sandboxFor, usePlugins } from '../plugins/runtime';
import { useDescribe } from '../ai/describe';
import { searchCatalog, useCatalog } from '../state/catalog';
import type { Module } from '../lib/modules';
import { useUi } from '../state/ui';
import { formatKeys, SHORTCUT_KEYS } from '../shortcuts';
import { CloseButton } from './CloseButton';
import { PageIcon } from './PageIcon';
import { useWorkspaceUi } from '../workspaces/state';

/** Breadcrumb path ("Parent / Child") so same-named pages can be told apart. */
function pathOf(page: PageRecord, byId: Map<string, PageRecord>): string {
  const parts: string[] = [];
  let p = page.parentId ? byId.get(page.parentId) : undefined;
  while (p && parts.length < 4) {
    parts.unshift(p.title);
    p = p.parentId ? byId.get(p.parentId) : undefined;
  }
  return parts.join(' / ');
}

interface CommandDef {
  id: string;
  label: string;
  icon: ReactNode;
  keys?: string;
  run: () => void | Promise<unknown>;
}

const matches = (label: string, q: string) => !q || label.toLowerCase().includes(q.toLowerCase());

/** Colour of a command's icon, by the module it belongs to. */
function moduleOf(id: string): Module {
  if (id.startsWith('plugin')) return 'plugins';
  const map: Record<string, Module> = {
    new: 'dashboard',
    dashboard: 'dashboard',
    graph: 'graph',
    ai: 'ai',
    sample: 'ai',
    'new-database': 'templates',
    template: 'templates',
    'templates-folder': 'templates',
    daily: 'tasks',
    capture: 'tasks',
    tasks: 'tasks',
    projects: 'projects',
    memos: 'memos',
    tips: 'projects',
  };
  return map[id] ?? 'neutral';
}

export function CommandPalette() {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const { paletteOpen, paletteMode, set, open, sidebarOpen, recent } = useUi();
  const byId = useCatalog((s) => s.byId);
  useCatalog((s) => s.aliases); // re-rank when aliases change
  const [query, setQuery] = useState('');
  const [templates, setTemplates] = useState<TemplateInfo[]>([]);
  const plugins = usePlugins();
  const describe = useDescribe();

  const [contentHits, setContentHits] = useState<{ q: string; hits: SearchHit[] } | null>(null);

  useEffect(() => {
    if (paletteOpen) void listAllTemplates(store).then(setTemplates);
  }, [paletteOpen, store]);

  // Full-text matches in page content, below the title matches.
  useEffect(() => {
    const q = query.trim();
    if (paletteMode !== 'all' || q.length < 3) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void store.search(q, 8).then((hits) => !cancelled && setContentHits({ q, hits }));
    }, 120);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query, paletteMode, store]);

  const close = () => {
    set({ paletteOpen: false, paletteMode: 'all' });
    setQuery('');
  };
  const run = (fn: () => void | Promise<unknown>) => () => {
    close();
    void fn();
  };
  const openPage = (page: PageRecord, newTab = false) =>
    open({ kind: 'page', pageId: page.id }, newTab);
  const createPage = async (title: string) => openPage(await store.createPage({ title }));

  const item =
    'flex cursor-pointer items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm aria-selected:bg-bg-hover';
  const group =
    'text-xs text-fg-muted [&_[cmdk-group-items]]:text-fg [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1';

  const commands: CommandDef[] = [
    {
      id: 'new',
      label: t('sidebar.newPage'),
      icon: <Plus className="size-4" />,
      keys: SHORTCUT_KEYS.newPage,
      run: () => createPage(''),
    },
    {
      id: 'dashboard',
      label: t('dashboard.label'),
      icon: <LayoutDashboard className="size-4" />,
      run: () => open({ kind: 'dashboard' }),
    },
    {
      id: 'graph',
      label: t('graph.title'),
      icon: <Waypoints className="size-4" />,
      run: () => open({ kind: 'graph' }),
    },
    {
      id: 'ai',
      label: t('ai.command'),
      icon: <Sparkles className="size-4" />,
      keys: SHORTCUT_KEYS.ai,
      run: () => set({ aiOpen: true }),
    },
    {
      id: 'new-database',
      label: t('commands.newDatabase'),
      icon: <Database className="size-4" />,
      run: async () => openPage(await createDatabase(store, { title: '' })),
    },
    {
      id: 'daily',
      label: t('commands.dailyNote'),
      icon: <CalendarDays className="size-4" />,
      keys: SHORTCUT_KEYS.dailyNote,
      run: async () => openPage(await ensureDailyNote(store, i18n.language)),
    },
    {
      id: 'template',
      label: t('commands.fromTemplate'),
      icon: <LayoutTemplate className="size-4" />,
      // Re-open in template mode after this close.
      run: () => setTimeout(() => set({ paletteOpen: true, paletteMode: 'templates' })),
    },
    {
      id: 'templates-folder',
      label: t('commands.templatesFolder'),
      icon: <LayoutTemplate className="size-4" />,
      run: () => open({ kind: 'templates' }),
    },
    {
      id: 'capture',
      label: t('capture.title'),
      icon: <ListPlus className="size-4" />,
      keys: SHORTCUT_KEYS.capture,
      run: () => set({ captureOpen: true }),
    },
    {
      id: 'tasks',
      label: t('tasks.title'),
      icon: <CheckSquare className="size-4" />,
      keys: SHORTCUT_KEYS.tasks,
      run: () => open({ kind: 'tasks', tab: 'today' }),
    },
    {
      id: 'projects',
      label: t('projects.title'),
      icon: <FolderKanban className="size-4" />,
      run: () => open({ kind: 'projects' }),
    },
    {
      id: 'workspace-create',
      label: t('workspaces.create'),
      icon: <Users className="size-4" />,
      run: () => useWorkspaceUi.getState().openCreate(),
    },
    {
      id: 'tips',
      label: t('tips.title'),
      icon: <Lightbulb className="size-4" />,
      run: () => open({ kind: 'tips' }),
    },
    {
      id: 'memos',
      label: t('memos.title'),
      icon: <Pin className="size-4" />,
      run: () => open({ kind: 'memos' }),
    },
    {
      id: 'search',
      label: t('search.title'),
      icon: <Search className="size-4" />,
      keys: SHORTCUT_KEYS.search,
      run: () => open({ kind: 'search', query: '' }),
    },
    {
      id: 'import',
      label: t('import.command'),
      icon: <Upload className="size-4" />,
      run: () => open({ kind: 'import' }),
    },
    {
      id: 'nexus-import',
      label: t('nexusImport.command'),
      icon: <Upload className="size-4" />,
      run: () => open({ kind: 'nexus-import' }),
    },
    {
      id: 'reorganize',
      label: t('reorganize.command'),
      icon: <FolderTree className="size-4" />,
      run: () => set({ reorganizeOpen: true }),
    },
    {
      id: 'export',
      label: t('export.command'),
      icon: <Download className="size-4" />,
      run: () => exportToZip(store),
    },
    {
      id: 'trash',
      label: t('sidebar.trash'),
      icon: <Trash2 className="size-4" />,
      run: () => open({ kind: 'trash' }),
    },
    {
      id: 'sidebar',
      label: t('shortcuts.toggleSidebar'),
      icon: <PanelLeft className="size-4" />,
      keys: SHORTCUT_KEYS.toggleSidebar,
      run: () => set({ sidebarOpen: !sidebarOpen }),
    },
    {
      id: 'settings',
      label: t('sidebar.settings'),
      icon: <Settings className="size-4" />,
      keys: SHORTCUT_KEYS.settings,
      run: () => set({ settingsOpen: true }),
    },
    {
      id: 'sample',
      label: t('sidebar.loadSample'),
      icon: <Sparkles className="size-4" />,
      run: () => seedWorkspace(store),
    },
    {
      id: 'plugins',
      label: t('plugins.title'),
      icon: <Puzzle className="size-4" />,
      run: () => open({ kind: 'plugins' }),
    },
    // Commands added by enabled plugins (ADR 0025).
    ...(plugins ?? [])
      .filter((p) => p.enabled)
      .flatMap((p) =>
        p.manifest.commands.map((c) => ({
          id: `plugin:${p.pageId}:${c.id}`,
          label: `${c.title} (${p.manifest.name})`,
          icon: <Puzzle className="size-4" />,
          run: async () => {
            try {
              const box = await sandboxFor(store, p, describe);
              const out = await box.run('command', c.id);
              if (typeof out === 'string' && out.trim()) pluginToast(p.manifest.name, out);
            } catch (e) {
              pluginToast(p.manifest.name, e instanceof Error ? e.message : String(e));
            }
          },
        })),
      ),
  ];

  // Nothing typed yet: recently visited pages first.
  const recentPages =
    paletteMode !== 'templates' && !query.trim()
      ? recent.flatMap((id) => byId.get(id) ?? []).slice(0, 8)
      : [];
  const recentIds = new Set(recentPages.map((p) => p.id));
  const pages =
    paletteMode === 'templates' ? [] : searchCatalog(query, 50).filter((p) => !recentIds.has(p.id));
  const titleIds = new Set(pages.map((p) => p.id));
  const hits =
    paletteMode === 'all' && contentHits && contentHits.q === query.trim()
      ? contentHits.hits.filter((h) => !titleIds.has(h.pageId))
      : [];
  const shownCommands =
    paletteMode === 'all' ? commands.filter((c) => matches(c.label, query)) : [];
  const shownTemplates =
    paletteMode === 'templates'
      ? templates.filter((x) => matches(`${x.page.title} ${x.database?.title ?? ''}`, query))
      : [];
  const placeholder =
    paletteMode === 'pages'
      ? t('palette.switcher')
      : paletteMode === 'templates'
        ? t('palette.templates')
        : t('palette.placeholder');

  return (
    <Command.Dialog
      open={paletteOpen}
      onOpenChange={(o) => (o ? set({ paletteOpen: true }) : close())}
      label={t('palette.label')}
      shouldFilter={false}
      overlayClassName="fixed inset-0 z-40 bg-black/30"
      contentClassName="nx-pop-in fixed inset-x-0 top-[12vh] z-50 mx-auto w-[min(40rem,calc(100vw-2rem))] overflow-hidden rounded-2xl border border-border bg-bg shadow-pop"
    >
      <div className="flex items-center gap-2 border-b border-border pr-3">
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder={placeholder}
          className="min-w-0 flex-1 bg-transparent px-5 py-4 text-base outline-none focus-visible:outline-none"
        />
        <CloseButton onClick={close} />
      </div>
      <Command.List className="max-h-[50vh] overflow-y-auto p-2">
        <Command.Empty className="px-2 py-3 text-sm text-fg-muted">
          {paletteMode === 'templates' && !query
            ? t('palette.noTemplates')
            : t('palette.noResults')}
        </Command.Empty>
        {shownTemplates.length > 0 && (
          <Command.Group heading={t('palette.templatesGroup')} className={group}>
            {shownTemplates.map((tpl) => (
              <Command.Item
                key={tpl.page.id}
                value={`tpl:${tpl.page.id}`}
                onSelect={run(() => openUseTemplate({ templateId: tpl.page.id, mode: 'page' }))}
                className={item}
              >
                <FilePlus2 className="size-4 text-fg-muted" />
                <span className="truncate">{tpl.page.title || t('page.untitled')}</span>
                {tpl.database && (
                  <span className="ml-auto truncate text-xs text-fg-muted">
                    {tpl.database.title}
                  </span>
                )}
              </Command.Item>
            ))}
          </Command.Group>
        )}
        {recentPages.length > 0 && (
          <Command.Group heading={t('palette.recent')} className={group}>
            {recentPages.map((p) => {
              const path = pathOf(p, byId);
              return (
                <Command.Item
                  key={p.id}
                  value={`recent:${p.id}`}
                  onSelect={run(() => openPage(p))}
                  className={item}
                >
                  <span className="grid size-4 place-items-center text-fg-muted">
                    <PageIcon icon={p.icon} fallback={<FileText className="size-4" />} />
                  </span>
                  <span className="truncate">{p.title || t('page.untitled')}</span>
                  {path && <span className="truncate text-xs text-fg-muted">{path}</span>}
                </Command.Item>
              );
            })}
          </Command.Group>
        )}
        {pages.length > 0 && (
          <Command.Group
            heading={t('palette.pages')}
            className={`${recentPages.length ? 'mt-2 ' : ''}${group}`}
          >
            {pages.map((p) => {
              const path = pathOf(p, byId);
              return (
                <Command.Item
                  key={p.id}
                  value={`page:${p.id}`}
                  onSelect={run(() => openPage(p))}
                  className={item}
                >
                  <span className="grid size-4 place-items-center text-fg-muted">
                    <PageIcon icon={p.icon} fallback={<FileText className="size-4" />} />
                  </span>
                  <span className="truncate">{p.title || t('page.untitled')}</span>
                  {path && <span className="truncate text-xs text-fg-muted">{path}</span>}
                </Command.Item>
              );
            })}
          </Command.Group>
        )}
        {hits.length > 0 && (
          <Command.Group heading={t('palette.inContent')} className={`mt-2 ${group}`}>
            {hits.map((h) => (
              <Command.Item
                key={h.pageId}
                value={`hit:${h.pageId}`}
                onSelect={run(() => open({ kind: 'page', pageId: h.pageId }))}
                className={`${item} flex-col items-start gap-0.5`}
              >
                <span className="flex items-center gap-2">
                  <FileText className="size-4 text-fg-muted" />
                  <span className="truncate">{h.title || t('page.untitled')}</span>
                </span>
                <span className="line-clamp-1 pl-6 text-xs text-fg-muted">
                  <Snippet text={h.snippet} />
                </span>
              </Command.Item>
            ))}
            <Command.Item
              value="search-all"
              onSelect={run(() => open({ kind: 'search', query: query.trim() }))}
              className={item}
            >
              <Search className="size-4" />
              {t('palette.searchAll', { query: query.trim() })}
            </Command.Item>
          </Command.Group>
        )}
        {paletteMode !== 'templates' && (query.trim() || shownCommands.length > 0) && (
          <Command.Group heading={t('palette.commands')} className={`mt-2 ${group}`}>
            {query.trim() && (
              <Command.Item
                value="create"
                onSelect={run(() => createPage(query.trim()))}
                className={item}
              >
                <Plus className="size-4" />
                {t('palette.create', { title: query.trim() })}
              </Command.Item>
            )}
            {shownCommands.map((c) => (
              <Command.Item key={c.id} value={`cmd:${c.id}`} onSelect={run(c.run)} className={item}>
                <span
                  aria-hidden
                  className={`mod-icon mod-${moduleOf(c.id)} size-7 [&>svg]:size-3.5`}
                >
                  {c.icon}
                </span>
                <span className="flex-1">{c.label}</span>
                {c.keys && <kbd className="text-xs text-fg-muted">{formatKeys(c.keys)}</kbd>}
              </Command.Item>
            ))}
          </Command.Group>
        )}
      </Command.List>
    </Command.Dialog>
  );
}
